// src/pages/Dashboard.jsx
import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../shared/supabase";
import logo from "../images/isuzulogowhite.png"; // adjust path if needed

export default function Dashboard() {
  const [reviews, setReviews] = useState([]);
  const [totalFeedbacks, setTotalFeedbacks] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [startDate, setStartDate] = useState("");
const [endDate, setEndDate] = useState("");
const exportToCSV = async () => {
  try {
    let query = supabase
      .from("isuzu_feedback")
      .select("*")
      .order("created_at", { ascending: false });

    if (startDate) {
      query = query.gte("created_at", `${startDate}T00:00:00`);
    }
    if (endDate) {
      query = query.lte("created_at", `${endDate}T23:59:59`);
    }

    const { data, error } = await query;
    if (error) throw error;

    if (!data || data.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = Object.keys(data[0]);

    const csvRows = [
      headers.join(","), // header row
      ...data.map(row =>
        headers
          .map(field =>
            `"${String(row[field] ?? "").replace(/"/g, '""')}"`
          )
          .join(",")
      ),
    ];

    const csvBlob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(csvBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `isuzu_feedback_${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("CSV export failed:", err);
  }
};


  const reviewsPerPage = 30;
  const subscribedRef = useRef(false);
  const originalTitle = "Isuzu Feedback Dashboard";

  const copyToClipboard = (text, fieldId) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 1000);
  };

  const formatDate = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "------";

  // Fetch total feedback count
  const fetchStats = async () => {
    try {
      const { count, error } = await supabase
        .from("isuzu_feedback")
        .select("*", { count: "exact" });
      if (error) throw error;
      setTotalFeedbacks(count);
    } catch (err) {
      console.error("Error fetching total feedback:", err);
    }
  };

  // Fetch reviews
  const fetchReviews = async (page = 1) => {
    setLoading(true);
    try {
      const startIndex = (page - 1) * reviewsPerPage;
      const { data, error } = await supabase
        .from("isuzu_feedback")
        .select("*")
        .order("created_at", { ascending: false })
        .range(startIndex, startIndex + reviewsPerPage - 1);

      if (error) throw error;
      setReviews(data || []);
      setCurrentPage(page);

      const { count } = await supabase
        .from("isuzu_feedback")
        .select("*", { count: "exact" });
      setTotalPages(Math.ceil(count / reviewsPerPage));
    } catch (err) {
      console.error("Error fetching reviews:", err);
    } finally {
      setLoading(false);
    }
  };

  // Realtime subscription
  const setupRealtimeSubscription = () => {
  if (subscribedRef.current) return;

  const channel = supabase
    .channel("feedback-changes")
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "isuzu_feedback" },
      (payload) => {
        console.log("Realtime payload received:", payload); // 🔥 check this
        setReviews((prev) => [payload.new, ...prev]);
        setTotalFeedbacks((prev) => prev + 1);

        if (!document.hasFocus()) {
          document.title = `1 New Feedback - ${originalTitle}`;
        }
      }
    )
    .subscribe((status) => {
      console.log("Subscription status:", status); // 🔥 should be SUBSCRIBED
      if (status === "SUBSCRIBED") subscribedRef.current = true;
    });

  return channel;
};


  useEffect(() => {
    const handleFocus = () => (document.title = originalTitle);
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  useEffect(() => {
    const initialize = async () => {
      await fetchStats();
      await fetchReviews(1);
    };
    initialize();

    const channel = setupRealtimeSubscription();
    return () => {
      if (channel) supabase.removeChannel(channel);
      subscribedRef.current = false;
    };
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) fetchReviews(newPage);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages)
      startPage = Math.max(1, endPage - maxVisiblePages + 1);

    pages.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
      >
        Previous
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded ${currentPage === i ? "bg-red-600 text-white" : "bg-gray-200"}`}
        >
          {i}
        </button>
      );
    }

    pages.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
      >
        Next
      </button>
    );

    return pages;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6 bg-white p-4 rounded-xl shadow">
        <img src={logo} alt="Isuzu Logo" className="h-12" />
        <input
          type="text"
          placeholder="Search reviews..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 mx-4 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-300 outline-none"
        />
        <div className="text-center px-4 py-2 bg-red-600 text-white rounded-xl font-semibold">
          Total Feedbacks: {totalFeedbacks}
        </div>
        <div className="flex items-center gap-3">
  <input
    type="date"
    value={startDate}
    onChange={(e) => setStartDate(e.target.value)}
    className="px-3 py-2 border rounded-lg"
  />

  <input
    type="date"
    value={endDate}
    onChange={(e) => setEndDate(e.target.value)}
    className="px-3 py-2 border rounded-lg"
  />

  <button
    onClick={exportToCSV}
    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
  >
    Export CSV
  </button>
</div>

      </div>

      {/* Reviews Table */}
      {loading ? (
        <div className="text-center py-20">Loading reviews...</div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left">Customer</th>
                <th className="px-4 py-2 text-left">Contact</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Vehicle Reg</th>
                <th className="px-4 py-2">First Fix?</th>
                <th className="px-4 py-2">Advisor Friendliness</th>
                <th className="px-4 py-2">Appointment Ease</th>
                <th className="px-4 py-2">Parts Arrangement</th>
                <th className="px-4 py-2">On-time Delivery</th>
                <th className="px-4 py-2">Facility Rating</th>
                <th className="px-4 py-2">General Feedback</th>
                <th className="px-4 py-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {reviews
                .filter(
                  (r) =>
                    r.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (r.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (r.general_feedback || "").toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 cursor-pointer transition">
                    <td className="px-4 py-2">{r.customer_name}</td>

                    <td
                      className="px-4 py-2 relative cursor-pointer text-red-600 font-semibold hover:underline"
                      onClick={() => copyToClipboard(r.contact, `contact-${r.id}`)}
                    >
                      {r.contact}
                      {copiedField === `contact-${r.id}` && (
                        <span className="absolute top-0 right-0 bg-black text-white text-xs px-2 py-1 rounded-full animate-fadeIn">
                          Copied
                        </span>
                      )}
                    </td>

                    <td
                      className="px-4 py-2 relative cursor-pointer text-red-600 font-semibold hover:underline"
                      onClick={() => r.email && copyToClipboard(r.email, `email-${r.id}`)}
                    >
                      {copiedField === `email-${r.id}` ? "Copied" : r.email || "------"}
                    </td>

                    <td className="px-4 py-2">{r.vehicle_reg}</td>
                    <td className="px-4 py-2">{r.q1_first_time_fix}</td>
                    <td className="px-4 py-2">{r.q2_advisor_friendliness}</td>
                    <td className="px-4 py-2">{r.q3_appointment_ease}</td>
                    <td className="px-4 py-2">{r.q4_parts_arrangement}</td>
                    <td className="px-4 py-2">{r.q5_on_time_delivery}</td>
                    <td className="px-4 py-2">{r.q6_facility_rating}</td>
                    <td className="px-4 py-2">{r.general_feedback || "------"}</td>
                    <td className="px-4 py-2">{formatDate(r.created_at)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-center mt-4 gap-2">{renderPagination()}</div>
    </div>
  );
}
